import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { ArrowLeft, Globe, Clock, TrendingUp, Loader2, ExternalLink, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function Analytics() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (id) loadData(); }, [id]);

  async function loadData() {
    try {
      const [a, p] = await Promise.all([api.getAnalytics(id!), api.getProject(id!)]);
      setData(a.analytics);
      setProject(p.project);
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  }

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-accent" size={24} /></div>;
  if (!data) return <div className="p-6 text-center"><h2>No data</h2><Link to={`/project/${id}`} className="text-accent">Back</Link></div>;

  const chartData = (data.top_pages || []).map((p: any) => ({
    name: p.path === '/' ? 'Home' : (p.path || '/').slice(0, 25),
    visits: p.count || p.c || 0
  }));

  const projectUrl = project ? `https://${project.subdomain}.meganhost.dpdns.org` : '';

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <Link to={`/project/${id}`} className="flex items-center gap-2 text-text-muted hover:text-text-primary mb-6">
        <ArrowLeft size={16} /> Back to project
      </Link>

      <h1 className="text-xl font-bold mb-6">Analytics</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="card py-3 px-4">
          <Globe size={18} className="text-accent-blue mb-2" />
          <p className="text-2xl font-bold">{(data.total_visits || 0).toLocaleString()}</p>
          <p className="text-xs text-text-muted">Total Visits</p>
        </div>
        <div className="card py-3 px-4">
          <Clock size={18} className="text-accent mb-2" />
          <p className="text-2xl font-bold">{(data.today || 0).toLocaleString()}</p>
          <p className="text-xs text-text-muted">Today</p>
        </div>
        <div className="card py-3 px-4">
          <TrendingUp size={18} className="text-accent-purple mb-2" />
          <p className="text-2xl font-bold">{(data.week || 0).toLocaleString()}</p>
          <p className="text-xs text-text-muted">This Week</p>
        </div>
        <div className="card py-3 px-4">
          <Calendar size={18} className="text-warning mb-2" />
          <p className="text-2xl font-bold">{(data.month || 0).toLocaleString()}</p>
          <p className="text-xs text-text-muted">This Month</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="card mb-6">
          <h2 className="font-semibold text-sm mb-4">Top Pages</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #1A1A1A', borderRadius: 8, fontSize: 12, color: '#FAFAFA' }} />
              <Bar dataKey="visits" fill="#22C55E" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Countries */}
      {(data.countries || []).length > 0 && (
        <div className="card mb-6">
          <h2 className="font-semibold text-sm mb-3">Countries</h2>
          <div className="space-y-2">
            {(data.countries || []).map((c: any) => (
              <div key={c.country} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 text-xs">
                <span>{c.country || 'Unknown'}</span>
                <span className="text-text-muted">{c.count || c.c || 0} visits</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project Info */}
      <div className="card">
        <h2 className="font-semibold text-sm mb-3">Project Info</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
          <div><p className="text-text-muted">Files</p><p className="font-semibold">{data.project?.files || 0}</p></div>
          <div><p className="text-text-muted">Storage</p><p className="font-semibold">{formatBytes(data.project?.storage_bytes || 0)}</p></div>
          <div><p className="text-text-muted">Status</p><span className={`badge text-[10px] ${data.project?.status==='deployed'?'badge-success':'badge-muted'}`}>{data.project?.status || 'draft'}</span></div>
        </div>
        {projectUrl && (
          <div className="mt-4 pt-3 border-t border-border">
            <a href={projectUrl} target="_blank" rel="noopener noreferrer" className="text-accent text-xs hover:underline flex items-center gap-1">
              {projectUrl} <ExternalLink size={11} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (!b) return '0 B';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
